/** Overview "Top domains" ranking — the top domains under the selected site,
 * from /waf/home/top-domains. Only shown for a single site + All domain. */
import { useMemo, useState } from 'react';
import { Globe } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardBody, SegmentedControl, Skeleton } from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { BarCard } from '@/pages/services/_shared/AnalyticsKit';
import { useWafHomeTopDomains, type WafHomeScope } from '@/api/waf';
import { fmtBytes, fmtNum } from './format';

type CdnMetric = 'traffic' | 'requests';

export function TopDomainsCard({ serviceId, scope }: { serviceId: string; scope: WafHomeScope }) {
  const [metric, setMetric] = useState<CdnMetric>('traffic');
  const q = useWafHomeTopDomains(serviceId, scope, metric === 'traffic' ? 'traffic_bytes' : 'requests');
  const rows = q.data ?? [];

  const items = useMemo(
    () =>
      rows
        .map((r) => ({
          label: r.domain_name || '—',
          value: metric === 'traffic' ? r.total_traffic_bytes ?? 0 : r.total_requests ?? 0,
        }))
        .filter((r) => r.value > 0)
        .sort((a, b) => b.value - a.value)
        .map((r) => ({ label: r.label, value: r.value, display: metric === 'traffic' ? fmtBytes(r.value) : fmtNum(r.value) })),
    [rows, metric],
  );

  return (
    <Card>
      <CardHeader
        actions={
          <SegmentedControl
            items={[{ value: 'traffic', label: 'Traffic' }, { value: 'requests', label: 'Requests' }]}
            value={metric}
            onValueChange={(v) => setMetric(v as CdnMetric)}
            size="sm"
            aria-label="Metric"
          />
        }
      >
        <CardTitle>Top domains</CardTitle>
      </CardHeader>
      <CardBody>
        {q.isPending ? (
          <Skeleton style={{ height: 200 }} />
        ) : items.length === 0 ? (
          <EmptyState size="sm" icon={<Globe size={20} />} title="No top domains yet" description="No domain traffic recorded in the selected period." />
        ) : (
          <BarCard title="" items={items} />
        )}
      </CardBody>
    </Card>
  );
}

export default TopDomainsCard;
